/**
 * SHOWICT Quantum — motor de detección (INTOCABLE).
 *
 * Motor extraído byte-idéntico del indicador legacy `showi_ict`
 * (frontend/src/indicators/showi_ict/index.js líneas 14-147, verificado por
 * diff de los cuerpos de función). SHOWICT Quantum es solo su presentación
 * Quantum: la lógica de detección NO se modifica aquí — cualquier cambio en
 * calcATR / calcRawPivots / runShowiICT es un cambio de comportamiento del
 * indicador y está prohibido dentro de este rediseño.
 *
 * El render legacy NO vive aquí: la pintura Quantum está en renderer.js y la
 * derivación de niveles (entry/SL/TP, capa nueva) en adapter.js.
 */

// ── ATR (Wilder smoothing, igual que ta.atr en Pine) ─────────────
function calcATR(candles, period) {
  const n = candles.length
  const trs = candles.map((c, i) =>
    i === 0
      ? c.high - c.low
      : Math.max(c.high - c.low, Math.abs(c.high - candles[i - 1].close), Math.abs(c.low - candles[i - 1].close))
  )
  const atrs = new Array(n).fill(0)
  const seed = trs.slice(0, Math.min(period, n)).reduce((a, b) => a + b, 0) / Math.min(period, n)
  for (let i = 0; i < period && i < n; i++) atrs[i] = seed
  for (let i = period; i < n; i++) atrs[i] = (atrs[i - 1] * (period - 1) + trs[i]) / period
  return atrs
}

// ── Pivots crudos (ta.pivothigh / ta.pivotlow) ────────────────────
function calcRawPivots(candles, pivotLen) {
  const n = candles.length
  const rawHighs = new Array(n).fill(null)
  const rawLows  = new Array(n).fill(null)
  for (let i = pivotLen; i < n - pivotLen; i++) {
    let isH = true, isL = true
    for (let j = i - pivotLen; j <= i + pivotLen; j++) {
      if (j === i) continue
      if (candles[j].high >= candles[i].high) isH = false
      if (candles[j].low  <= candles[i].low)  isL = false
    }
    if (isH) rawHighs[i] = candles[i].high
    if (isL) rawLows[i]  = candles[i].low
  }
  return { rawHighs, rawLows }
}

// ── Motor principal ───────────────────────────────────────────────
function runShowiICT(candles, config) {
  const { pivotLen, atrMult, atrLen } = config
  const n = candles.length
  if (n < pivotLen * 2 + atrLen + 5) return null

  const atrs               = calcATR(candles, atrLen)
  const { rawHighs, rawLows } = calcRawPivots(candles, pivotLen)

  // Estado (igual que las var de Pine Script)
  let activeH = null, activeL = null
  let activeHBar = -1, activeLBar = -1
  let usedH = false,  usedL  = false
  let usedFH = false, usedFL = false
  let bullBias = true
  let prevSwH = null, prevSwL = null

  const pivotLabels = []   // { time, price, label, isHigh }
  const signals     = []   // { time, type, levelPrice, levelTime, label, atr }

  for (let bar = 0; bar < n; bar++) {
    const c = candles[bar]

    // ── Confirmar pivots en este bar (el pivot real está pivotLen atrás) ──
    const pIdx = bar - pivotLen
    if (pIdx >= 1 && pIdx < n - 1) {
      if (rawHighs[pIdx] !== null) {
        const ph   = rawHighs[pIdx]
        const size = ph - Math.max(candles[pIdx + 1].close, candles[pIdx - 1].close)
        if (size >= atrMult * atrs[pIdx]) {
          prevSwH    = activeH
          activeH    = ph
          activeHBar = pIdx
          usedH      = false
          usedFH     = false
        }
      }
      if (rawLows[pIdx] !== null) {
        const pl   = rawLows[pIdx]
        const size = Math.min(candles[pIdx + 1].close, candles[pIdx - 1].close) - pl
        if (size >= atrMult * atrs[pIdx]) {
          prevSwL    = activeL
          activeL    = pl
          activeLBar = pIdx
          usedL      = false
          usedFL     = false
        }
      }
    }

    if (activeH === null && activeL === null) continue

    const atr = atrs[bar]

    // ── A: cuerpo rompe ───────────────────────────────────────────
    const bodyBreakU = activeH !== null && !usedH  && c.close > activeH
    const bodyBreakD = activeL !== null && !usedL  && c.close < activeL

    // ── F: mecha supera, cuerpo rechaza ──────────────────────────
    const fakeBreakU = activeH !== null && !usedH && !usedFH && c.high > activeH && c.close <= activeH
    const fakeBreakD = activeL !== null && !usedL && !usedFL && c.low  < activeL && c.close >= activeL

    if (bodyBreakU) {
      const isBos = bullBias
      const isHH  = prevSwH !== null && activeH > prevSwH
      pivotLabels.push({ time: candles[activeHBar].time, price: activeH, label: isHH ? 'HH' : 'LH', isHigh: true })
      signals.push({
        time: c.time, type: isBos ? 'bosU' : 'chochU',
        levelPrice: activeH, levelTime: candles[activeHBar].time,
        label: isBos ? 'A+' : 'A', atr,
      })
      usedH = true; usedFH = true
      if (!isBos) bullBias = true
    }

    if (bodyBreakD) {
      const isBos = !bullBias
      const isHL  = prevSwL !== null && activeL > prevSwL
      pivotLabels.push({ time: candles[activeLBar].time, price: activeL, label: isHL ? 'HL' : 'LL', isHigh: false })
      signals.push({
        time: c.time, type: isBos ? 'bosD' : 'chochD',
        levelPrice: activeL, levelTime: candles[activeLBar].time,
        label: isBos ? 'A-' : 'A', atr,
      })
      usedL = true; usedFL = true
      if (!isBos) bullBias = false
    }

    if (fakeBreakU) {
      signals.push({ time: c.time, type: 'fakeU', levelPrice: activeH, levelTime: candles[activeHBar].time, label: 'F', atr })
      usedFH = true
    }

    if (fakeBreakD) {
      signals.push({ time: c.time, type: 'fakeD', levelPrice: activeL, levelTime: candles[activeLBar].time, label: 'F', atr })
      usedFL = true
    }
  }

  return { signals, pivotLabels, activeH, activeL, bullBias }
}

// Defaults del motor: idénticos al defaultConfig del legacy showi_ict. El
// rediseño solo cambia la presentación, no la detección.
const defaultConfig = {
  pivotLen: 5,
  atrMult: 1.5,
  atrLen: 14,
}

// detect = runShowiICT (mismo nombre de frontera que el molde PRQ).
// calcATR/calcRawPivots se exportan también porque la capa de derivación de
// SL del adapter reutiliza la condición de tamaño del motor con la MISMA
// semántica ATR/pivot (los cuerpos de función siguen siendo verbatim).
const detect = runShowiICT

export { detect, defaultConfig, calcRawPivots, calcATR }
